<template>
  <div>
    <panel-navigation>
      <h1 class="text-lg font-semibold mb-4">Billing</h1>
      <hr class="border-b border-gray-100 mb-3">

      <v-skeleton-loader v-if="!me" type="article, article, article"></v-skeleton-loader>

      <div v-else>
        <v-alert
          v-if="successMessage"
          border="top"
          colored-border
          type="success"
          elevation="2"
          class="mb-4"
        >
          {{ successMessage }}
        </v-alert>

        <v-alert
          v-if="errorMessage"
          border="top"
          colored-border
          type="error"
          elevation="2"
          class="mb-4"
        >
          {{ errorMessage }}
        </v-alert>

        <div class="flex flex-row flex-wrap">
          <!-- products -->
          <div class="w-full lg:w-2/3 pr-0 lg:pr-6">
            <h2 class="text-gray-900 mb-4">Products</h2>

            <div class="flex flex-row flex-wrap">
              <div
                v-for="(product, index) in products"
                :key="'billing-product-' + index"
                class="w-full md:w-1/2 pr-2 md:pr-6 mb-4"
              >
                <div class="dashboard-box">
                  <v-card
                    class="pa-2"
                    tile
                    :style="isProductEnabled(product.key)
                      ? 'border: solid 3px #DDDDDD !important;'
                      : 'border: solid 3px #FFFFFF !important;'"
                  >
                    <div class="p-3">
                      <div style="text-align: center;">
                        <strong>{{ product.name }}</strong>
                        <br /><br />
                        <strong style="font-size: 24px;">
                          ${{ product.price.toFixed(2) }}
                          <span style="font-size: 12px; color: #757B89;">/ month</span>
                        </strong>
                        <br /><br />

                        <div
                          class="inline-block rounded px-3 py-1 text-xs font-bold"
                          :class="isProductEnabled(product.key) ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'"
                        >
                          {{ isProductEnabled(product.key) ? 'ENABLED' : 'DISABLED' }}
                        </div>

                        <div v-if="isProductEnabled(product.key) && getBillDay(product.key)" class="text-xs text-gray-500 mt-2">
                          Bills monthly on day {{ getBillDay(product.key) }}
                        </div>
                      </div>

                      <div class="mb-2 mt-6">
                        <strong style="font-size: 12px;">Features</strong>
                      </div>

                      <p
                        class="text-sm"
                        v-for="(feature, featureIndex) in product.features"
                        :key="'product-feature-' + index + '-' + featureIndex"
                      >
                        <i class="mdi mdi-check text-green-600"></i>&nbsp;&nbsp;&nbsp;{{ feature }}
                      </p>

                      <div style="text-align: center; padding-top: 40px; padding-bottom: 20px;">
                        <div
                          v-if="isProductEnabled(product.key)"
                          class="bg-danger text-white text-md text-bold inline-block rounded py-3 px-4 cursor-pointer"
                          :class="{ 'opacity-50 cursor-not-allowed': togglingProduct }"
                          @click="confirmToggleProduct(product, false)"
                        >
                          Disable
                        </div>

                        <div
                          v-else
                          class="bg-blue text-white text-md text-bold inline-block rounded py-3 px-4 cursor-pointer"
                          :class="{ 'opacity-50 cursor-not-allowed': togglingProduct }"
                          @click="confirmToggleProduct(product, true)"
                        >
                          Enable
                        </div>
                      </div>
                    </div>
                  </v-card>
                </div>
              </div>
            </div>
          </div>

          <!-- payment method -->
          <div class="w-full lg:w-1/3 mt-9">
            <div class="dashboard-box">
              <v-card class="pa-2" tile>
                <div class="p-3">
                  <div style="text-align: center;">
                    <strong>Payment Method</strong>
                    <br /><br />
                  </div>

                  <div v-if="loadingPaymentMethod" class="text-sm text-gray-600 text-center py-6">
                    Loading payment method...
                  </div>

                  <div v-else>
                    <div v-if="paymentMethod && paymentMethod.last4" class="bg-gray-100 rounded p-4 mb-4">
                      <div class="text-sm mb-2">
                        <strong>Saved Card</strong>
                      </div>

                      <div class="text-sm text-gray-700">
                        {{ formatCardBrand(paymentMethod.brand) }} ending in
                        <strong>{{ paymentMethod.last4 }}</strong>
                      </div>

                      <div class="text-sm text-gray-600 mt-1" v-if="paymentMethod.exp_month && paymentMethod.exp_year">
                        Expires {{ paymentMethod.exp_month }}/{{ paymentMethod.exp_year }}
                      </div>

                      <div class="text-xs text-gray-500 mt-1" v-if="paymentMethod.funding">
                        Type: {{ paymentMethod.funding }}
                      </div>
                    </div>

                    <div v-else class="bg-gray-100 rounded p-4 mb-4">
                      <div class="text-sm text-gray-700">
                        No credit card on file.
                      </div>
                    </div>

                    <div class="text-sm text-gray-600 mb-4">
                      Charges for enabled products will be billed to the card on file.
                    </div>

                    <div style="text-align: center;">
                      <div
                        class="bg-blue text-white text-xs text-bold inline-block rounded py-2 px-3 cursor-pointer"
                        @click="openCardModal"
                      >
                        <i class="fa fa-credit-card mr-2"></i>
                        {{ paymentMethod && paymentMethod.last4 ? 'UPDATE CARD' : 'ADD CARD' }}
                      </div>
                    </div>

                    <hr class="my-5" />

                    <div>
                      <div class="text-sm mb-2">
                        <strong>Current Monthly Total</strong>
                      </div>

                      <div class="text-2xl font-bold">
                        ${{ currentMonthlyTotal.toFixed(2) }}
                        <span class="text-sm text-gray-500 font-normal">/ month</span>
                      </div>
                    </div>
                  </div>
                </div>
              </v-card>
            </div>
          </div>
        </div>

        <!-- card modal -->
        <v-dialog v-model="showCardDialog" max-width="600">
          <v-card>
            <div class="p-6">
              <div class="flex justify-between items-center mb-4">
                <h2 class="text-lg font-semibold">Add Payment Method</h2>
                <button @click="closeCardModal" class="text-gray-500">✕</button>
              </div>

              <div v-if="cardError" class="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4">
                {{ cardError }}
              </div>

              <div class="mb-3 text-sm text-gray-600">
                Enter your card details below. Your card will be securely stored with Stripe.
              </div>

              <div class="border rounded p-4 bg-white">
                <div id="card-element"></div>
              </div>

              <div class="mt-6 text-right">
                <button
                  class="rounded border border-gray-300 bg-white text-gray-700 text-xs py-2 px-4 mr-2"
                  @click="closeCardModal"
                  :disabled="savingCard"
                >
                  Cancel
                </button>

                <button
                  class="rounded bg-blue text-white text-xs py-2 px-4"
                  @click="saveCard"
                  :disabled="savingCard"
                >
                  {{ savingCard ? 'Saving...' : 'Save Card' }}
                </button>
              </div>
            </div>
          </v-card>
        </v-dialog>
      </div>
    </panel-navigation>
  </div>
</template>

<script>
import axios from 'axios'
import { mapState } from 'vuex'
import PanelNavigation from '@/components/PanelNavigation.vue'

export default {
  components: {
    PanelNavigation
  },

  metaInfo() {
    return {
      title: 'Billing | ' + this.$appName
    }
  },

  data() {
    return {
      products: [
        {
          key: 'lsa_command',
          name: 'LSA Command',
          price: 200,
          features: [
            'Connect your Google LSA CID',
            'Pull in LSA stats and leads',
            'Create reports',
            'Submit LSA surveys',
            'Access all LSA-related tools'
          ]
        },
        {
          key: 'convertly',
          name: 'Convertly',
          price: 100,
          features: [
            'AI agent for calls',
            'AI agent for SMS',
            'AI web chat widget',
            'AI message handling',
            'Access all AI-related tools'
          ]
        }
      ],

      paymentMethod: null,
      loadingPaymentMethod: false,
      togglingProduct: false,
      successMessage: null,
      errorMessage: null,

      showCardDialog: false,
      savingCard: false,
      cardError: null,

      stripe: null,
      elements: null,
      cardElement: null,
      cardClientSecret: null
    }
  },

  computed: {
    ...mapState({
      me: state => state.me.me
    }),

    currentMonthlyTotal() {
      let total = 0
      if (this.me && this.me.lsa_command_enabled) total += 50
      if (this.me && this.me.convertly_enabled) total += 250
      return total
    }
  },

  methods: {
    isProductEnabled(productKey) {
      if (!this.me) return false
      if (productKey === 'lsa_command') return !!this.me.lsa_command_enabled
      if (productKey === 'convertly') return !!this.me.convertly_enabled
      return false
    },

    getBillDay(productKey) {
      if (!this.me) return null
      if (productKey === 'lsa_command') return this.me.lsa_command_bill_day
      if (productKey === 'convertly') return this.me.convertly_bill_day
      return null
    },

    formatCardBrand(brand) {
      if (!brand) return 'Card'
      return brand.charAt(0).toUpperCase() + brand.slice(1)
    },

    async fetchPaymentMethod() {
      this.loadingPaymentMethod = true

      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_ENDPOINT}/billing/payment-method`,
          {
            headers: {
              Authorization: 'Bearer ' + this.$store.state.auth.jwt
            }
          }
        )

        this.paymentMethod = response.data.payment_method || null
      } catch (e) {
        this.paymentMethod = null
      }

      this.loadingPaymentMethod = false
    },

    async confirmToggleProduct(product, shouldEnable) {
      if (this.togglingProduct) return

      const result = await this.$swal({
        title: shouldEnable ? 'Enable product?' : 'Disable product?',
        html: shouldEnable
          ? `This will enable <strong>${product.name}</strong> and bill <strong>$${product.price.toFixed(2)} / month</strong>.`
          : `This will disable <strong>${product.name}</strong> and stop future billing for this product.`,
        showCancelButton: true,
        confirmButtonColor: shouldEnable ? '#2563EB' : '#DD6B55',
        confirmButtonText: shouldEnable ? 'Yes, Enable' : 'Yes, Disable',
        cancelButtonText: 'Cancel'
      })

      if (!result.isConfirmed) return

      this.toggleProduct(product, shouldEnable)
    },

    async toggleProduct(product, shouldEnable) {
      this.togglingProduct = true
      this.successMessage = null
      this.errorMessage = null

      try {
        await axios.post(
          `${process.env.VUE_APP_API_ENDPOINT}/billing/toggle-product`,
          {
            product: product.key,
            enabled: shouldEnable
          },
          {
            headers: {
              Authorization: 'Bearer ' + this.$store.state.auth.jwt
            }
          }
        )

        await this.$store.dispatch('me/fetch')

        this.successMessage = shouldEnable
          ? `${product.name} enabled successfully.`
          : `${product.name} disabled successfully.`
      } catch (e) {
        this.errorMessage = e.response.data.message || 'Unable to update product.'
      }

      this.togglingProduct = false
    },

    async openCardModal() {
      this.cardError = null
      this.showCardDialog = true

      await this.$nextTick()
      await this.loadStripeCardForm()
    },

    closeCardModal() {
      this.showCardDialog = false
      this.cardError = null

      if (this.cardElement) {
        this.cardElement.unmount()
        this.cardElement = null
      }

      this.elements = null
      this.stripe = null
      this.cardClientSecret = null
    },

    async loadStripeCardForm() {
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_API_ENDPOINT}/billing/payment-method/setup-intent`,
          {},
          {
            headers: {
              Authorization: 'Bearer ' + this.$store.state.auth.jwt
            }
          }
        )

        const publishableKey = response.data.publishable_key
        this.cardClientSecret = response.data.client_secret

        if (!window.Stripe) {
          this.cardError = 'Stripe.js is not loaded.'
          return
        }

        this.stripe = window.Stripe(publishableKey)
        this.elements = this.stripe.elements()

        this.cardElement = this.elements.create('card', {
          hidePostalCode: true
        })

        this.cardElement.mount('#card-element')
      } catch (e) {
        this.cardError = e.response.data.message || 'Unable to initialize card form.'
      }
    },

    async saveCard() {
      this.savingCard = true
      this.cardError = null

      try {
        const result = await this.stripe.confirmCardSetup(this.cardClientSecret, {
          payment_method: {
            card: this.cardElement,
            billing_details: {
              name: `${this.me.first_name || ''} ${this.me.last_name || ''}`.trim(),
              email: this.me.email || null,
              phone: this.me.phone || null
            }
          }
        })

        if (result.error) {
          this.cardError = result.error.message
          this.savingCard = false
          return
        }

        const paymentMethodId = result.setupIntent.payment_method

        const response = await axios.post(
          `${process.env.VUE_APP_API_ENDPOINT}/billing/payment-method/attach`,
          {
            payment_method_id: paymentMethodId
          },
          {
            headers: {
              Authorization: 'Bearer ' + this.$store.state.auth.jwt
            }
          }
        )

        this.paymentMethod = response.data.payment_method
        this.successMessage = 'Payment method saved successfully.'
        this.closeCardModal()
      } catch (e) {
        this.cardError = e.response.data.message || 'Unable to save payment method.'
      }

      this.savingCard = false
    }
  },

  async mounted() {
    await this.$store.dispatch('me/fetch')
    await this.fetchPaymentMethod()
  }
}
</script>

<style type="text/css">
  .v-dialog {
    height: auto !important;
  }
</style>